import React from "react";
import axios from "axios";
import config from "../../config";
import Swal from "sweetalert2";

const SHIPPING_TASK_KEY = "global_shipping_task";

const ShippingProgressBar = () => {
  const [task, setTask] = React.useState(null);
  const [logData, setLogData] = React.useState(null);
  const [showLogs, setShowLogs] = React.useState(false);
  const [activeLogTab, setActiveLogTab] = React.useState("ok");

  /*
   * =========================================================
   * LOAD TASK
   * =========================================================
   */
  const loadTask = React.useCallback(() => {
    try {
      const saved = localStorage.getItem(SHIPPING_TASK_KEY);

      if (!saved) {
        setTask(null);
        setLogData(null);
        return;
      }

      const parsed = JSON.parse(saved);

      if (!parsed?.log_id) {
        localStorage.removeItem(SHIPPING_TASK_KEY);
        setTask(null);
        setLogData(null);
        return;
      }

      setTask(parsed);
    } catch (error) {
      console.error("Shipping task load error:", error);

      localStorage.removeItem(SHIPPING_TASK_KEY);

      setTask(null);
      setLogData(null);
    }
  }, []);

  /*
   * =========================================================
   * INITIAL LOAD + EVENTS
   * =========================================================
   */
  React.useEffect(() => {
    loadTask();

    const handleShippingStarted = () => {
      loadTask();
    };

    const handleStorage = (event) => {
      if (event.key === SHIPPING_TASK_KEY || event.key === null) {
        loadTask();
      }
    };

    window.addEventListener("shipping-task-started", handleShippingStarted);

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        "shipping-task-started",
        handleShippingStarted
      );

      window.removeEventListener("storage", handleStorage);
    };
  }, [loadTask]);

  /*
   * =========================================================
   * POLL BACKEND
   * =========================================================
   */
  React.useEffect(() => {
    if (!task?.log_id) {
      return undefined;
    }

    let cancelled = false;

    const fetchShippingStatus = async () => {
      try {
        const response = await axios.post(
          `${config.apiUrl}/shipment/bulk_shipment_log_view`,
          {
            log_id: task.log_id,
          },
          {
            headers: config.headers,
          }
        );

        if (cancelled || !response.data?.status) {
          return;
        }

        setLogData(response.data);

        const output = Array.isArray(response.data?.output)
          ? response.data.output[0]
          : {};

        const total = Number(
          output?.total_records ??
            response.data?.total_records ??
            task?.total ??
            1
        );

        const success = Number(response.data?.success_count || 0);

        const failed = Number(response.data?.failed_count || 0);

        const processed = success + failed;

        const progress =
          total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

        const completed = total > 0 && processed >= total;

        const updatedTask = {
          ...task,
          total,
          success_count: success,
          failed_count: failed,
          processed,
          progress,
          done: completed,
        };

        localStorage.setItem(SHIPPING_TASK_KEY, JSON.stringify(updatedTask));

        setTask(updatedTask);
      } catch (error) {
        console.error("Shipping progress polling error:", error);
      }
    };

    fetchShippingStatus();

    const interval = setInterval(fetchShippingStatus, 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [task?.log_id]);

  /*
   * =========================================================
   * NO ACTIVE TASK
   * =========================================================
   */
  if (!task) {
    return null;
  }

  /*
   * =========================================================
   * CURRENT VALUES
   * =========================================================
   */
  const output = Array.isArray(logData?.output) ? logData.output : [];

  const masterLog = output[0] || {};

  const total = Number(
    masterLog?.total_records ?? logData?.total_records ?? task?.total ?? 1
  );

  const success = Number(logData?.success_count ?? task?.success_count ?? 0);

  const failed = Number(logData?.failed_count ?? task?.failed_count ?? 0);

  const processed = success + failed;

  const progress =
    total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  const isCompleted = total > 0 && processed >= total;

  /*
   * =========================================================
   * CLOSE
   * =========================================================
   */
  const handleClose = () => {
    localStorage.removeItem(SHIPPING_TASK_KEY);

    setTask(null);
    setLogData(null);
    setShowLogs(false);

    window.dispatchEvent(new Event("shipping-task-closed"));
  };

  /*
   * =========================================================
   * OPEN LOGS
   * =========================================================
   */
  const openLogs = (tab = "ok") => {
    setActiveLogTab(tab);
    setShowLogs(true);
  };

  const handleShipAgainFromLogs = (failedEntries) => {
    if (!Array.isArray(failedEntries) || failedEntries.length === 0) {
      return;
    }

    /*
     * Convert failed log entries back into
     * the order shape expected by the
     * Ship Orders modal.
     */
    const retryOrders = failedEntries
      .map((entry) => ({
        ...entry,

        record_type: "order",

        order_number: entry?.order_number || entry?.order_id || "",

        order_id: entry?.order_number || entry?.order_id || "",
      }))
      .filter((item) => item.order_number);

    if (!retryOrders.length) {
      Swal.fire({
        icon: "warning",
        text: "Unable to identify the failed orders.",
      });

      return;
    }

    /*
     * Close logs.
     */
    setShowLogs(false);

    /*
     * Notify Orders page.
     *
     * We cannot directly call Orders.jsx
     * state setters from here because this
     * component is global.
     */
    window.dispatchEvent(
      new CustomEvent("shipping-retry-orders", {
        detail: {
          orders: retryOrders,
        },
      })
    );
  };

  return (
    <>
      {/* =====================================================
          ORIGINAL ELITE SHIPPING RESULT STRUCTURE
          ===================================================== */}
      <div
        className="ol-ship-result show"
        id="olShipResultBar"
        aria-hidden="false"
        style={{
          position: "fixed",
          right: "24px",
          left: "auto",
          bottom: "18px",
          width: "70%",
          maxWidth: "1500px",
          zIndex: 99999,
        }}
      >
        <div className="ol-ship-result-accent"></div>

        {/* =================================================
            PROGRESS
            ================================================= */}
        <div
          className="ol-ship-result-prog"
          id="olShipProgWrap"
          hidden={isCompleted}
        >
          <div className="ol-ship-progress-top">
            <span className="ol-ship-prog-state">
              <i className="bi bi-truck"></i>

              <span id="olShipProgLabel">
                Processing {processed}/{total}…
              </span>
            </span>

            <b id="olShipProgPct">{progress}%</b>
          </div>

          <div className="progress ol-ship-progbar">
            <div
              className="progress-bar"
              id="olShipProgBar"
              style={{
                width: `${progress}%`,
              }}
            ></div>
          </div>
        </div>

        {/* =================================================
            COMPLETED RESULT
            ================================================= */}
        <div
          className="ol-ship-result-row"
          id="olShipResRow"
          hidden={!isCompleted}
        >
          <span className="ol-ship-result-title">
            <i className="bi bi-send-check"></i>
            Bulk Shipping Result
          </span>

          <span className="ol-ship-result-stats">
            <span className="ol-ship-stat">
              <b id="olResTotal">{total}</b> Total
            </span>

            <button
              type="button"
              className="ol-ship-stat ok"
              id="olResOkBtn"
              data-logtab="ok"
              title="View successful shipments"
              onClick={() => openLogs("ok")}
            >
              <b id="olResOk">{success}</b> Success
            </button>

            <button
              type="button"
              className="ol-ship-stat bad"
              id="olResBadBtn"
              data-logtab="bad"
              title="View failed shipments"
              onClick={() => openLogs("bad")}
            >
              <b id="olResBad">{failed}</b> Failed
            </button>
          </span>

          <span className="ol-ship-result-flex"></span>

          <button
            type="button"
            className="ol-btn ol-btn-bar"
            id="olShipViewLogs"
            onClick={() => openLogs("ok")}
          >
            <i className="bi bi-clock-history"></i>
            View Logs
          </button>

          <button
            type="button"
            className="ol-ship-result-close"
            id="olShipResultClose"
            aria-label="Close"
            onClick={handleClose}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      </div>

      {/* =====================================================
          LOG MODAL
          ===================================================== */}
      {showLogs && (
        <ShippingLogsModal
          data={logData}
          initialTab={activeLogTab}
          onTabChange={setActiveLogTab}
          onClose={() => setShowLogs(false)}
          onShipAgain={handleShipAgainFromLogs}
        />
      )}
    </>
  );
};

/* ===========================================================
   SHIPPING LOGS MODAL
   =========================================================== */

const ShippingLogsModal = ({
  data,
  initialTab,
  onTabChange,
  onClose,
  onShipAgain,
}) => {
  const [activeTab, setActiveTab] = React.useState(
    initialTab === "bad" ? "bad" : "ok"
  );
  const [selectedFailed, setSelectedFailed] = React.useState([]);

  const [selectedSuccess, setSelectedSuccess] = React.useState([]);

  React.useEffect(() => {
    setActiveTab(initialTab === "bad" ? "bad" : "ok");
  }, [initialTab]);

  const output = Array.isArray(data?.output) ? data.output : [];

  const masterLog = output[0] || {};

  const logEntries = Array.isArray(masterLog?.log) ? masterLog.log : [];

  const successLogs = logEntries.filter(
    (entry) => String(entry?.status) === "1"
  );

  const failedLogs = logEntries.filter(
    (entry) => String(entry?.status) === "2"
  );

  const total = Number(masterLog?.total_records ?? data?.total_records ?? 0);

  /*
   * =========================================================
   * HELPERS
   * =========================================================
   */

  const getRowId = (entry, index) =>
    String(
      entry?.shipment_id ||
        entry?.order_number ||
        entry?.order_id ||
        entry?._id ||
        index
    );

  const getProduct = (entry) => {
    if (entry?.product) {
      return entry.product;
    }

    if (entry?.product_name) {
      return entry.product_name;
    }
    if (entry?.service_provider) {
      return entry.service_provider;
    }

    if (entry?.item_name) {
      return entry.item_name;
    }

    if (Array.isArray(entry?.items) && entry.items.length > 0) {
      return entry.items[0]?.item_name || "—";
    }

    return "—";
  };

  const getAWB = (entry) =>
    entry?.carrier_tracking_number || entry?.awb_number || entry?.awb || "—";

  const getCourier = (entry) => {
    console.log("entry==", entry);
    const value =
      entry?.service_provider ??
      entry?.carrier_name ??
      entry?.courier_name ??
      masterLog?.courier;
    if (value == undefined || value === null || value === "") {
      return "-";
    }
    return value;
  };

  const getWeight = (entry) => {
    const value = entry?.weight ?? entry?.total_weight ?? entry?.charged_weight;

    if (value === undefined || value === null || value === "") {
      return "—";
    }

    return Number(value).toFixed(2);
  };

  const getDestination = (entry) => {
    const receiver = Array.isArray(entry?.receiver)
      ? entry.receiver[0]
      : entry?.receiver;

    if (receiver) {
      return [receiver.city, receiver.state, receiver.pincode]
        .filter(Boolean)
        .join(", ");
    }

    return entry?.destination || "—";
  };

  const getPayment = (entry) => {
    const paymentMode = String(
      entry?.payment_mode || entry?.payment || ""
    ).toLowerCase();

    if (paymentMode === "cod") {
      return (
        <>
          ₹{Number(entry?.cod_amount || 0).toLocaleString("en-IN")}
          <em className="ol-ship-logcod">COD</em>
        </>
      );
    }

    return <span className="ol-ship-chip blue">Prepaid</span>;
  };

  /*
   * =========================================================
   * SUCCESS SELECTION
   * =========================================================
   */

  const toggleSuccess = (entry, index) => {
    const id = getRowId(entry, index);

    setSelectedSuccess((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const allSuccessSelected =
    successLogs.length > 0 && selectedSuccess.length === successLogs.length;

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSuccess(
        successLogs.map((entry, index) => getRowId(entry, index))
      );
    } else {
      setSelectedSuccess([]);
    }
  };

  /*
   * =========================================================
   * TAB CHANGE
   * =========================================================
   */

  const changeTab = (tab) => {
    setActiveTab(tab);

    if (onTabChange) {
      onTabChange(tab);
    }

    if (tab === "bad") {
      setSelectedSuccess([]);
    }
  };

  /*
   * =========================================================
   * DOWNLOAD LABEL
   * =========================================================
   */

  const downloadLabels = () => {
    const selectedShipmentIds = successLogs
      .filter((entry, index) =>
        selectedSuccess.includes(getRowId(entry, index))
      )
      .map((entry) => entry?.shipment_id)
      .filter(Boolean);

    if (selectedShipmentIds.length === 0) {
      Swal.fire({
        icon: "warning",
        text: "Please select at least one shipment.",
      });
      return;
    }

    window.open(
      `/print_label?id=${selectedShipmentIds.join(",")}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  /*
   * =========================================================
   * META
   * =========================================================
   */

  const logId = masterLog?.log_id || data?.log_id || "";

  const batchText = logId ? `#${String(logId).slice(-4)}` : "#0";

  const courier = getCourier(successLogs[0] || failedLogs[0] || {});

  /*
   * =========================================================
   * MODAL
   * =========================================================
   */

  const toggleFailed = (entry, index) => {
    const id = getRowId(entry, index);

    setSelectedFailed((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };
  const allFailedSelected =
    failedLogs.length > 0 && selectedFailed.length === failedLogs.length;

  const handleSelectAllFailed = (checked) => {
    if (checked) {
      setSelectedFailed(
        failedLogs.map((entry, index) => getRowId(entry, index))
      );
    } else {
      setSelectedFailed([]);
    }
  };
  const handleShipAgain = () => {
    const retryRows = failedLogs.filter((entry, index) =>
      selectedFailed.includes(getRowId(entry, index))
    );

    if (!retryRows.length) {
      return;
    }

    if (typeof onShipAgain === "function") {
      onShipAgain(retryRows);
    }
  };
  return (
    <>
      {/* Bootstrap backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{
          zIndex: 1050,
        }}
        onClick={onClose}
      ></div>

      <div
        className="modal fade show ol-bulk-modal ol-ship-modal ol-ship-modal-xl"
        id="olModalShipLogs"
        tabIndex="-1"
        aria-hidden="false"
        role="dialog"
        aria-modal="true"
        style={{
          display: "block",
          zIndex: 1060,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            {/* =================================================
                  HEADER
                  ================================================= */}

            <div className="modal-header">
              <div>
                <h5 className="modal-title">
                  <i className="bi bi-clock-history"></i> Shipping Logs
                </h5>

                <p className="ol-bulk-sub">
                  Batch <span id="olLogBatch">{batchText}</span>
                  {" · "}
                  <span id="olLogMeta">
                    {total} order
                    {total !== 1 ? "s" : ""}
                  </span>
                  {" · "}
                  <span id="olLogCourier">{courier}</span>
                </p>
              </div>

              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              ></button>
            </div>

            {/* =================================================
                  BODY
                  ================================================= */}

            <div className="modal-body">
              {/* =================================================
                    TABS
                    ================================================= */}

              <div className="ol-ship-tabs" role="tablist">
                <button
                  type="button"
                  className={`ol-ship-tab ${activeTab === "ok" ? "on" : ""}`}
                  data-logtab="ok"
                  role="tab"
                  onClick={() => changeTab("ok")}
                >
                  <i className="bi bi-check2-circle"></i> Success{" "}
                  <span className="ol-ship-tab-n" id="olLogOkN">
                    {data?.success_count ?? successLogs.length}
                  </span>
                </button>

                <button
                  type="button"
                  className={`ol-ship-tab ${activeTab === "bad" ? "on" : ""}`}
                  data-logtab="bad"
                  role="tab"
                  onClick={() => changeTab("bad")}
                >
                  <i className="bi bi-x-octagon"></i> Failed{" "}
                  <span className="ol-ship-tab-n bad" id="olLogBadN">
                    {data?.failed_count ?? failedLogs.length}
                  </span>
                </button>
              </div>

              {/* =================================================
                    SUCCESS PANEL
                    ================================================= */}

              <div
                className="ol-ship-logpanel"
                id="olLogPanelOk"
                hidden={activeTab !== "ok"}
              >
                <div className="ol-ship-logbar">
                  <label className="ol-ship-logsel">
                    <input
                      type="checkbox"
                      id="olLogSelAll"
                      checked={allSuccessSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />

                    <span>Select all</span>
                  </label>

                  <span className="ol-ship-logcount" id="olLogSelCount">
                    {selectedSuccess.length} selected
                  </span>

                  <span className="ol-ship-logflex"></span>

                  <span className="ol-ship-loghint">
                    <i className="bi bi-info-circle"></i> Select rows to
                    download labels
                  </span>
                </div>

                <div className="ol-ship-logscroll">
                  <table className="table ol-ship-logtable">
                    <thead>
                      <tr>
                        <th className="ol-ship-logchk"></th>
                        <th>Order</th>
                        {/* <th>Product</th> */}
                        <th>Courier / AWB</th>
                        {/* <th>Weight</th> */}
                        <th>Payment</th>
                        {/* <th>Destination</th> */}
                      </tr>
                    </thead>

                    <tbody id="olShipLogOkList">
                      {successLogs.map((entry, index) => {
                        const rowId = getRowId(entry, index);

                        return (
                          <tr key={rowId} className="ol-ship-logrow">
                            <td className="ol-ship-logchk">
                              <input
                                type="checkbox"
                                className="ol-ship-logcheck"
                                data-logid={rowId}
                                checked={selectedSuccess.includes(rowId)}
                                onChange={() => toggleSuccess(entry, index)}
                              />
                            </td>

                            <td className="ol-ship-logid">
                              <b>
                                {entry?.order_number || entry?.order_id || "—"}
                              </b>
                            </td>

                            {/* <td>{getProduct(entry)}</td> */}

                            <td>
                              <span className="ol-ship-logawb">
                                {getAWB(entry)}
                              </span>

                              {/* <small>{getCourier(entry)}</small> */}
                            </td>

                            {/* <td>{getWeight(entry)} kg</td> */}

                            <td>{getPayment(entry)}</td>

                            {/* <td>{getDestination(entry)}</td> */}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {successLogs.length === 0 && (
                    <div className="ol-ship-log-empty text-center m-2">
                      <h6> No successful shipments</h6>
                    </div>
                  )}
                </div>
              </div>

              {/* =================================================
                    FAILED PANEL
                    ================================================= */}

              <div
                className="ol-ship-logpanel"
                id="olLogPanelBad"
                hidden={activeTab !== "bad"}
              >
                <div className="ol-ship-logbar">
                  <label className="ol-ship-logsel">
                    <input
                      type="checkbox"
                      id="olLogSelAllBad"
                      checked={allFailedSelected}
                      onChange={(e) => handleSelectAllFailed(e.target.checked)}
                    />

                    <span>Select all</span>
                  </label>
                  <span className="ol-ship-logcount">
                    {failedLogs.length} failed
                  </span>

                  <span className="ol-ship-logflex"></span>
                  <button
                    type="button"
                    className="btn btn-sm ol-ship-logretry"
                    id="olLogShipAgainBad"
                    disabled={selectedFailed.length === 0}
                    onClick={handleShipAgain}
                  >
                    <i className="bi bi-send"></i>
                    Ship Again
                  </button>
                </div>

                <div className="ol-ship-logscroll">
                  <table className="table ol-ship-logtable">
                    <thead>
                      <tr>
                        <th className="ol-ship-logchk"></th>
                        <th>Order</th>
                        {/* <th>Product</th> */}
                        <th>Reason</th>
                        {/* <th>Weight</th> */}
                        <th>Payment</th>
                      </tr>
                    </thead>

                    <tbody id="olShipLogBadList">
                      {failedLogs.map((entry, index) => {
                        const rowId = getRowId(entry, index);

                        return (
                          <tr key={rowId} className="ol-ship-logrow bad">
                            <td className="ol-ship-logchk">
                              <input
                                type="checkbox"
                                className="ol-ship-logcheck"
                                checked={selectedFailed.includes(rowId)}
                                onChange={() => toggleFailed(entry, index)}
                              />
                            </td>

                            <td className="ol-ship-logid">
                              <b>
                                {entry?.order_number || entry?.order_id || "—"}
                              </b>
                            </td>

                            {/* <td>
          {getProduct(entry)}
        </td> */}

                            <td>
                              <span className="ol-ship-logerr">
                                {entry?.reason ||
                                  entry?.message ||
                                  "Shipment booking failed."}
                              </span>
                            </td>

                            {/* <td>
          {getWeight(entry)} kg
        </td> */}

                            <td>{getPayment(entry)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {failedLogs.length === 0 && (
                    <div className="ol-ship-log-empty text-center m-2">
                      <h6>No failed shipments</h6>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* =================================================
                  FOOTER
                  ================================================= */}

            <div className="modal-footer">
              <span className="ol-ship-logflex"></span>

              {/* No Excel */}

              {/* Download Label ONLY on Success */}
              {activeTab === "ok" && (
                <button
                  type="button"
                  className="btn btn-primary"
                  id="olLogDownloadLabel"
                  disabled={selectedSuccess.length === 0}
                  onClick={downloadLabels}
                >
                  <i className="bi bi-tag"></i> Download Label
                </button>
              )}

              <button type="button" className="btn btn-light" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShippingProgressBar;
